/**
 * src/components/agent/ChatSidebarV2.jsx — Redesigned sidebar for the unified chat shell.
 *
 * Phase 82-03 — Chat System Unification, sidebar.
 *
 * Composition:
 *   - Header: "New chat" button (+ optional Close on mobile drawer)
 *   - Project filter pill → popover (All / Unassigned / <project>...)
 *   - Active conversations grouped: Today / Yesterday / Past 7 days / Older
 *   - Archived accordion (collapsed by default)
 *   - ConversationContextMenu for Move / Rename / Archive(+Restore) / Delete
 *
 * Data: useChatList hook (82-03 Task 1) owns the list + filter + refresh.
 * Mutations go straight to src/lib/api/agent.js; after each mutation the
 * sidebar calls list.refresh() to re-read from Supabase. The inline rename
 * path commits through ChatListItem's controlled-input mode.
 *
 * Hooks at top per Phase 90-07 rule. No raw hex per HARD DEPLOYMENT RULES.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import ChatListItem from './ChatListItem';
import ConversationContextMenu from './ConversationContextMenu';
import ScopeFilterPill from './ScopeFilterPill';
import { useChatList } from '../../lib/hooks/useChatList';
import { useAppDataContext } from '../../lib/AppDataContext';
import { useToast } from '../shared/ToastProvider';
import { useT } from '../../lib/i18n/I18nContext';
import {
  updateConversationProject,
  updateConversationTitle,
  deleteConversation,
  restoreConversation,
} from '../../lib/api/agent';
import { logAction, logResult } from '../../lib/observability';
import {
  rootStyle,
  headerStyle,
  newChatBtnStyle,
  listWrapStyle,
  groupHeaderStyle,
  accordionBtnStyle,
  emptyGroupStyle,
  loadingStyle,
  errorStyle,
  closeBtnStyle,
} from './ChatSidebarV2.styles';

function ChatGroup({ label, items, current, onSelect, onContextMenu, projectLookup, renamingId, onRenameCommit, onRenameCancel }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={groupHeaderStyle}>{label}</div>
      {items.map(c => (
        <ChatListItem
          key={c.id}
          conversation={c}
          active={c.id === current}
          projectName={c.project_id ? projectLookup?.[c.project_id] : null}
          renaming={c.id === renamingId}
          onClick={onSelect}
          onContextMenu={onContextMenu}
          onRenameCommit={title => onRenameCommit(c.id, title)}
          onRenameCancel={onRenameCancel}
        />
      ))}
    </div>
  );
}

export default function ChatSidebarV2({
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onClose,
  selectedProjectId = null,
}) {
  const list = useChatList();
  const { projects, projectLookup, deals, tasks, people, stages } = useAppDataContext() || {};
  const { showSuccess, showError, showInfo } = useToast();
  const { t } = useT();

  // TICKET-233 — unified scope filter groups: Projects / Pipeline (OPEN deals
  // only) / Tasks / People. The chat schema links all four (migrations
  // 046/047/087); this exposes them in one dropdown. Open deals = stage not
  // terminal (mirrors DealCard/PipelineKanban's `!is_terminal` rule).
  const scopeGroups = useMemo(() => {
    const stageById = {};
    (stages || []).forEach(s => { stageById[s.id] = s; });
    const openDeals = (deals || []).filter(d => {
      if (d.archived_at) return false;
      const st = stageById[d.stage_id];
      return !st || !st.is_terminal; // unknown stage → keep (don't hide silently)
    });
    const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
    return [
      { type: 'project', label: t('agentChat.groupProjects'),
        items: (projects || []).map(p => ({ id: p.id, name: p.project_name || p.display_name || t('agentChat.unnamed') })).sort(byName) },
      { type: 'deal', label: t('agentChat.groupPipeline'),
        items: openDeals.map(d => ({ id: d.id, name: d.opportunity || d.title || d.name || t('agentChat.unnamed') })).sort(byName) },
      { type: 'task', label: t('agentChat.groupTasks'),
        items: (tasks || []).filter(tk => !tk.archived_at).map(tk => ({ id: tk.id, name: tk.task_name || tk.title || t('agentChat.unnamed') })).sort(byName) },
      { type: 'person', label: t('agentChat.groupPeople'),
        items: (people || []).map(pe => ({ id: pe.id, name: pe.display_name || pe.email || t('agentChat.unnamed') })).sort(byName) },
    ].filter(g => g.items.length > 0);
  }, [projects, deals, tasks, people, stages, t]);

  const [menuState, setMenuState] = useState(null); // { x, y, conversation }
  const [renamingId, setRenamingId] = useState(null);

  // TICKET-107 (a) — the scope view filters by the SELECTED project instead
  // of always defaulting to "All projects": when the active chat has a
  // project scope (passed down from AgentShell), sync the sidebar filter to
  // it. Manual pill overrides stay in force until the selection changes
  // again; a null selection never yanks the filter.
  const setProjectFilter = list.setProjectFilter;
  useEffect(() => {
    if (selectedProjectId) setProjectFilter(selectedProjectId);
  }, [selectedProjectId, setProjectFilter]);

  const openMenu = useCallback(({ x, y, conversation }) => {
    setMenuState({ x, y, conversation });
  }, []);

  const closeMenu = useCallback(() => setMenuState(null), []);

  const handleRename = useCallback(conv => {
    setRenamingId(conv.id);
  }, []);

  const handleRenameCommit = useCallback(async (id, nextTitle) => {
    setRenamingId(null);
    const clean = (nextTitle || '').trim();
    if (!clean) return;
    const cid = logAction('chat:rename', { conversationId: id, title: clean });
    try {
      const row = await updateConversationTitle(id, clean);
      if (!row) throw new Error('Rename failed');
      logResult('chat:rename', 'success', { correlationId: cid, conversationId: id });
      showSuccess('Renamed');
      list.refresh();
      // Notify useChat (in AgentShell) so the header title updates immediately
      // without requiring a full conversation re-hydration.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('chat-conversation-renamed', { detail: { id, title: clean } })
        );
      }
    } catch (err) {
      logResult('chat:rename', 'error', { correlationId: cid, conversationId: id, error: err.message || String(err) });
      showError(`Rename failed: ${err.message || err}`);
    }
  }, [list, showSuccess, showError]);

  const handleRenameCancel = useCallback(() => {
    setRenamingId(null);
  }, []);

  const handleMoveToProject = useCallback(async (conv, projectId) => {
    // 2026-04-20 audit — every chat mutation now emits action_intent +
    // action_result so app_error_logs is the single source of truth for
    // "what did the CEO do in the chat sidebar" diagnostics.
    const cid = logAction('chat:move_to_project', { conversationId: conv.id, projectId });
    try {
      const row = await updateConversationProject(conv.id, projectId);
      if (!row) throw new Error('Move failed');
      showSuccess(projectId ? 'Moved to project' : 'Removed from project');
      list.refresh();
      logResult('chat:move_to_project', 'success', { correlationId: cid, conversationId: conv.id, projectId });
    } catch (err) {
      showError(`Move failed: ${err.message || err}`);
      logResult('chat:move_to_project', 'error', { correlationId: cid, conversationId: conv.id, error: err.message || String(err) });
    }
  }, [list, showSuccess, showError]);

  const handleArchive = useCallback(async conv => {
    const cid = logAction('chat:archive', { conversationId: conv.id });
    try {
      const row = await deleteConversation(conv.id);
      if (!row) throw new Error('Archive failed');
      list.refresh();
      logResult('chat:archive', 'success', { correlationId: cid, conversationId: conv.id });
      showInfo('Archived', {
        action: {
          label: 'Undo',
          onClick: async () => {
            const undoCid = logAction('chat:archive_undo', { conversationId: conv.id, correlationId: cid });
            try {
              await restoreConversation(conv.id);
              list.refresh();
              showSuccess('Restored');
              logResult('chat:archive_undo', 'success', { correlationId: undoCid, conversationId: conv.id });
            } catch (err) {
              showError(`Undo failed: ${err.message || err}`);
              logResult('chat:archive_undo', 'error', { correlationId: undoCid, conversationId: conv.id, error: err.message || String(err) });
            }
          },
        },
      });
    } catch (err) {
      showError(`Archive failed: ${err.message || err}`);
      logResult('chat:archive', 'error', { correlationId: cid, conversationId: conv.id, error: err.message || String(err) });
    }
  }, [list, showInfo, showSuccess, showError]);

  const handleRestore = useCallback(async conv => {
    const cid = logAction('chat:restore', { conversationId: conv.id });
    try {
      const row = await restoreConversation(conv.id);
      if (!row) throw new Error('Restore failed');
      showSuccess('Restored');
      list.refresh();
      logResult('chat:restore', 'success', { correlationId: cid, conversationId: conv.id });
    } catch (err) {
      showError(`Restore failed: ${err.message || err}`);
      logResult('chat:restore', 'error', { correlationId: cid, conversationId: conv.id, error: err.message || String(err) });
    }
  }, [list, showSuccess, showError]);

  const handleDelete = useCallback(async conv => {
    // CLAUDE.md "Data Integrity Rule": never hard-delete user data.
    // The 30-day UI gate is honored, but the mutation stays a soft-archive
    // restamp until a permanent-delete RLS policy ships.
    const cid = logAction('chat:delete', { conversationId: conv.id });
    try {
      const row = await deleteConversation(conv.id);
      if (!row) throw new Error('Delete failed');
      list.refresh();
      showInfo('Deleted from history');
      logResult('chat:delete', 'success', { correlationId: cid, conversationId: conv.id });
    } catch (err) {
      showError(`Delete failed: ${err.message || err}`);
      logResult('chat:delete', 'error', { correlationId: cid, conversationId: conv.id, error: err.message || String(err) });
    }
  }, [list, showInfo, showError]);

  return (
    <div style={rootStyle} data-testid="chat-sidebar-v2">
      <div style={headerStyle}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            style={newChatBtnStyle}
            onClick={onNewConversation}
            data-testid="chat-sidebar-new"
          >
            <Plus size={16} />
            New chat
          </button>
          {onClose && (
            <button
              type="button"
              style={closeBtnStyle}
              onClick={onClose}
              aria-label={t('agentChat.closeChatList')}
              data-testid="chat-sidebar-close"
            >
              <X size={20} />
            </button>
          )}
        </div>

        <ScopeFilterPill
          value={list.scopeFilter}
          groups={scopeGroups}
          onChange={list.setScopeFilter}
        />
      </div>

      <div style={listWrapStyle}>
        {list.loading && <div style={loadingStyle}>Loading chats…</div>}
        {list.error && <div style={errorStyle}>Couldn&apos;t load chats: {list.error}</div>}

        {!list.loading && !list.error && (
          <>
            {list.conversations.length === 0 && (
              <div style={emptyGroupStyle}>{t('agentChat.noChatsForFilter')}</div>
            )}

            {[
              ['Today', list.groups.today],
              ['Yesterday', list.groups.yesterday],
              ['Past 7 days', list.groups.pastWeek],
              ['Older', list.groups.older],
            ].map(([label, items]) => (
              <ChatGroup
                key={label}
                label={label}
                items={items}
                current={currentConversationId}
                onSelect={onSelectConversation}
                onContextMenu={openMenu}
                projectLookup={projectLookup}
                renamingId={renamingId}
                onRenameCommit={handleRenameCommit}
                onRenameCancel={handleRenameCancel}
              />
            ))}

            {list.archivedConversations.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  style={accordionBtnStyle}
                  onClick={() => list.setArchivedExpanded(v => !v)}
                  aria-expanded={list.archivedExpanded}
                  data-testid="chat-sidebar-archived-toggle"
                >
                  {list.archivedExpanded ? '▾' : '▸'} Archived ({list.archivedConversations.length})
                </button>
                {list.archivedExpanded && list.archivedConversations.map(c => (
                  <ChatListItem
                    key={c.id}
                    conversation={c}
                    active={c.id === currentConversationId}
                    projectName={c.project_id ? projectLookup?.[c.project_id] : null}
                    renaming={c.id === renamingId}
                    onClick={onSelectConversation}
                    onContextMenu={openMenu}
                    onRenameCommit={title => handleRenameCommit(c.id, title)}
                    onRenameCancel={handleRenameCancel}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <ConversationContextMenu
        menuState={menuState}
        projects={projects}
        onClose={closeMenu}
        onRename={handleRename}
        onMoveToProject={handleMoveToProject}
        onArchive={handleArchive}
        onRestore={handleRestore}
        onDelete={handleDelete}
      />
    </div>
  );
}
